/* ==========================================================================
   ÚNICO — JavaScript del tema
   Sin librerías externas. Cada funcionalidad es un Web Component independiente.
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const designMode = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Utilidades ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const formatMoney = (cents, format) => {
    format = format || (theme.showCurrencyCode ? theme.moneyWithCurrencyFormat : theme.moneyFormat) || '{{amount}} €';
    cents = Math.round(Number(cents) || 0);
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const fmt = (n, precision, thousands = ',', decimal = '.') => {
      const fixed = (n / 100).toFixed(precision);
      const [int, dec] = fixed.split('.');
      const withThousands = int.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      return dec ? withThousands + decimal + dec : withThousands;
    };
    const match = format.match(placeholder);
    if (!match) return String(cents / 100);
    let value;
    switch (match[1]) {
      case 'amount_no_decimals': value = fmt(cents, 0); break;
      case 'amount_with_comma_separator': value = fmt(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fmt(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = fmt(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = fmt(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = fmt(cents, 0, ' ', ','); break;
      default: value = fmt(cents, 2);
    }
    return format.replace(placeholder, value);
  };
  theme.formatMoney = formatMoney;

  const toast = (message) => {
    const el = $('.toast');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };
  theme.toast = toast;

  const onVisible = (el, cb, options = { rootMargin: '0px 0px -10% 0px' }) => {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { io.disconnect(); cb(); }
      });
    }, options);
    io.observe(el);
  };

  /* ---------- Carrito (API AJAX de Shopify) ---------- */
  const Cart = {
    drawer() { return $('cart-drawer'); },

    sectionIds(extra = []) {
      const ids = new Set(extra);
      if (this.drawer()) ids.add('cart-drawer');
      $$('[data-cart-contents][data-section-id]').forEach((el) => { if (el.dataset.sectionId) ids.add(el.dataset.sectionId); });
      return Array.from(ids);
    },

    async request(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: this.sectionIds(), sections_url: window.location.pathname })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || theme.strings.error);
      }
      return data;
    },

    async add(items) {
      const data = await this.request(theme.routes.cartAdd + '.js', { items });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async change(key, quantity) {
      const data = await this.request(theme.routes.cartChange + '.js', { id: key, quantity });
      this.render(data.sections);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      return data;
    },

    async updateNote(note) {
      await fetch(theme.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note })
      });
    },

    render(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-cart-contents]');
        if (!fresh) return;
        const current = $$('[data-cart-contents]').find((el) => el.dataset.sectionId === id);
        if (current) current.replaceWith(fresh);
        this.updateCount(fresh.dataset.count);
      });
    },

    updateCount(count) {
      if (count === undefined) return;
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.hidden = Number(count) === 0;
      });
    },

    afterAdd() {
      if (theme.cartType === 'checkout') {
        window.location.href = '/checkout';
      } else if (theme.cartType === 'page' || !this.drawer()) {
        window.location.href = theme.routes.cart;
      } else {
        this.drawer().open();
      }
    }
  };
  theme.Cart = Cart;

  /* ---------- Selector de cantidad (+ / -) ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = $('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty-minus], [data-qty-plus]');
        if (!btn || !this.input) return;
        const min = Number(this.input.min || 0);
        const step = Number(this.input.step || 1);
        const current = Number(this.input.value) || 0;
        const next = btn.hasAttribute('data-qty-plus') ? current + step : Math.max(min, current - step);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Carrito lateral ---------- */
  class CartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = $('.drawer__panel', this);
      this.onKey = (e) => { if (e.key === 'Escape') this.close(); };
      this.addEventListener('click', (e) => {
        const closer = e.target.closest('[data-drawer-close]');
        if (!closer) return;
        if (closer.tagName === 'A') {
          const url = new URL(closer.href, window.location.href);
          if (url.pathname !== window.location.pathname) return;
          e.preventDefault();
        }
        this.close();
      });
    }

    open() {
      this._lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKey);
      requestAnimationFrame(() => this.panel && this.panel.focus({ preventScroll: true }));
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKey);
      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus({ preventScroll: true });
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  // Interacciones dentro del carrito (lateral o página): cantidades, eliminar, venta cruzada y nota.
  const changeLine = debounce(async (item, quantity) => {
    item.classList.add('is-loading');
    try {
      await Cart.change(item.dataset.key, quantity);
    } catch (err) {
      item.classList.remove('is-loading');
      toast(err.message);
    }
  }, 350);

  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-line-qty]');
    if (qty) {
      const item = qty.closest('.cart-item');
      if (item) changeLine(item, Math.max(0, Number(qty.value) || 0));
      return;
    }
    const note = e.target.closest('[data-cart-note]');
    if (note) Cart.updateNote(note.value);
  });

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-line-remove]');
    if (remove) {
      const item = remove.closest('.cart-item');
      if (item) changeLine(item, 0);
      return;
    }
    const toggle = e.target.closest('[data-cart-toggle]');
    if (toggle && Cart.drawer() && theme.cartType === 'drawer') {
      e.preventDefault();
      Cart.drawer().open();
    }
  });

  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-upsell-form]');
    if (!form) return;
    e.preventDefault();
    const btn = $('button[type="submit"]', form);
    btn && btn.setAttribute('aria-busy', 'true');
    try {
      await Cart.add([{ id: Number(new FormData(form).get('id')), quantity: 1 }]);
      if (!form.closest('[data-cart-contents]')) {
        btn && btn.removeAttribute('aria-busy');
        Cart.afterAdd();
      }
    } catch (err) {
      toast(err.message);
      btn && btn.removeAttribute('aria-busy');
    }
  });

  /* ---------- Formulario de producto ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;
      this.button = $('[data-atc]', this.form);
      this.error = $('[data-form-error]', this.form);
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.button || this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
      const invalid = Array.from(this.form.elements).find((el) => el.willValidate && !el.checkValidity());
      if (invalid) { invalid.reportValidity(); return; }

      const data = new FormData(this.form);
      const main = { id: Number(data.get('id')), quantity: Math.max(1, Number(data.get('quantity')) || 1) };
      const properties = {};
      for (const [key, value] of data.entries()) {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && value !== '') properties[m[1]] = value;
      }
      if (Object.keys(properties).length) main.properties = properties;
      if (data.get('selling_plan')) main.selling_plan = Number(data.get('selling_plan'));

      const items = [main];
      const scope = this.closest('product-info') || document;
      $$('[data-addon]:checked', scope).forEach((addon) => items.push({ id: Number(addon.value), quantity: 1 }));

      this.setBusy(true);
      if (this.error) this.error.hidden = true;
      try {
        await Cart.add(items);
        Cart.afterAdd();
      } catch (err) {
        if (this.error) {
          this.error.textContent = err.message;
          this.error.hidden = false;
        } else {
          toast(err.message);
        }
      } finally {
        if (theme.cartType === 'drawer') this.setBusy(false);
      }
    }

    setBusy(busy) {
      if (!this.button) return;
      if (busy) this.button.setAttribute('aria-busy', 'true');
      else this.button.removeAttribute('aria-busy');
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Producto: variantes, packs, precios y stock ---------- */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = $('[data-product-json]', this);
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.form = document.getElementById(this.dataset.formId);
      this.variantInput = this.form && $('[data-variant-id]', this.form);
      this.qtyHidden = this.form && $('[data-qty-hidden]', this.form);
      this.mainQty = $('[data-main-qty]', this);
      this.variant = this.product.variants.find((v) => String(v.id) === String(this.variantInput && this.variantInput.value)) || this.product.variants[0];

      this.addEventListener('change', this.onChange.bind(this));
      const checkedPack = $('[data-pack]:checked', this);
      if (checkedPack) this.setQuantity(Number(checkedPack.dataset.qty));
      this.updateAvailability();
    }

    onChange(e) {
      const t = e.target;
      if (t.matches('[data-option-index]')) {
        this.onOptionChange();
      } else if (t.matches('[data-pack]')) {
        this.setQuantity(Number(t.dataset.qty));
      } else if (t.matches('[data-main-qty]')) {
        this.setQuantity(Math.max(1, Number(t.value) || 1));
      } else if (t.matches('[data-sticky-variant]')) {
        const v = this.product.variants.find((x) => String(x.id) === t.value);
        if (v) { this.syncPickers(v); this.setVariant(v); }
      }
    }

    selectedOptions() {
      const opts = [];
      $$('[data-variant-picker] select[data-option-index]', this).forEach((s) => { opts[Number(s.dataset.optionIndex)] = s.value; });
      $$('[data-variant-picker] input[data-option-index]:checked', this).forEach((i) => { opts[Number(i.dataset.optionIndex)] = i.value; });
      return opts;
    }

    onOptionChange() {
      const opts = this.selectedOptions();
      const v = this.product.variants.find((variant) => variant.options.every((o, i) => opts[i] === undefined || o === opts[i]));
      this.setVariant(v || null);
    }

    syncPickers(v) {
      v.options.forEach((value, i) => {
        $$(`[data-variant-picker] [data-option-index="${i}"]`, this).forEach((el) => {
          if (el.tagName === 'SELECT') el.value = value;
          else el.checked = el.value === value;
        });
      });
    }

    setVariant(v) {
      this.variant = v;
      // Texto de la opción seleccionada
      $$('[data-variant-picker] fieldset', this).forEach((fs) => {
        const checked = $('input:checked', fs);
        const label = $('[data-selected-value]', fs);
        if (checked && label) label.textContent = checked.value;
      });
      this.updateAvailability();
      this.updateButtons();
      if (!v) return;

      if (this.variantInput) {
        this.variantInput.value = v.id;
        this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (this.dataset.updateUrl === 'true') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url.toString());
      }
      const sticky = $('[data-sticky-variant]', this);
      if (sticky) sticky.value = v.id;

      this.updatePrices();
      this.updatePacks();
      this.updateInventory();
      if (v.featured_media_id) {
        const gallery = $('media-gallery', this);
        gallery && gallery.goToMedia && gallery.goToMedia(v.featured_media_id);
      }
    }

    updateAvailability() {
      const opts = this.selectedOptions();
      $$('[data-variant-picker] input[data-option-index], [data-variant-picker] select[data-option-index] option', this).forEach((el) => {
        const idx = Number(el.tagName === 'OPTION' ? el.parentElement.dataset.optionIndex : el.dataset.optionIndex);
        const value = el.value;
        const available = this.product.variants.some((v) => v.available && v.options[idx] === value &&
          v.options.every((o, i) => i === idx || opts[i] === undefined || o === opts[i]));
        el.classList.toggle('is-unavailable', !available);
      });
    }

    updateButtons() {
      const v = this.variant;
      const available = !!(v && v.available);
      const label = !v ? theme.strings.unavailable : available ? null : theme.strings.soldOut;
      $$('[data-atc], [data-sticky-submit]', this).forEach((btn) => {
        btn.disabled = !available;
        const span = $('[data-atc-label]', btn);
        if (!span) return;
        if (!span.dataset.original) span.dataset.original = span.textContent.trim();
        span.textContent = label || span.dataset.original;
      });
    }

    updatePrices() {
      const v = this.variant;
      $$('[data-price]', this).forEach((wrap) => {
        const current = $('[data-price-current]', wrap);
        const compare = $('[data-price-compare]', wrap);
        const badge = $('[data-price-badge]', wrap);
        const onSale = v.compare_at_price > v.price;
        wrap.classList.toggle('price--on-sale', onSale);
        if (current) current.textContent = formatMoney(v.price);
        if (compare) {
          compare.hidden = !onSale;
          compare.textContent = onSale ? formatMoney(v.compare_at_price) : '';
        }
        if (badge) {
          badge.hidden = !onSale;
          if (onSale) {
            const saving = v.compare_at_price - v.price;
            badge.textContent = theme.savingsDisplay === 'amount'
              ? theme.strings.saveAmount.replace('[amount]', formatMoney(saving).replace(/[.,]00(?!\d)/, ''))
              : theme.strings.savePercent.replace('[percent]', Math.round((saving * 100) / v.compare_at_price));
          }
        }
      });
    }

    updatePacks() {
      const v = this.variant;
      const compareBase = Math.max(v.compare_at_price || 0, v.price);
      $$('[data-pack]', this).forEach((input) => {
        const pack = input.closest('.pack');
        const qty = Number(input.dataset.qty) || 1;
        const disc = Number(input.dataset.discount) || 0;
        const total = Math.round((v.price * qty * (100 - disc)) / 100);
        const compare = compareBase * qty;
        const price = $('[data-pack-price]', pack);
        const cmp = $('[data-pack-compare]', pack);
        const unit = $('[data-pack-unit]', pack);
        const save = $('[data-pack-save]', pack);
        if (price) price.textContent = formatMoney(total);
        if (cmp) { cmp.hidden = compare <= total; cmp.textContent = formatMoney(compare); }
        if (unit) unit.textContent = theme.strings.perUnit.replace('[price]', formatMoney(Math.round(total / qty)));
        if (save) save.textContent = theme.strings.youSave.replace('[amount]', formatMoney(compare - total));
      });
    }

    updateInventory() {
      const box = $('[data-inventory]', this);
      if (!box) return;
      const inner = $('[data-inventory-inner]', box);
      const text = $('[data-inventory-text]', box);
      const bar = $('[data-inventory-bar]', box);
      const threshold = Number(box.dataset.threshold) || 0;
      const v = this.variant;
      let state = 'hidden';
      if (!v.available) {
        state = 'hidden';
      } else if (v.inventory !== null && v.inventory !== undefined) {
        if (v.inventory > 0 && v.inventory <= threshold) state = 'low';
        else if (v.inventory > threshold) state = 'ok';
      } else if (v.available) {
        state = 'ok';
      }
      if (state === 'ok' && !box.dataset.okText) state = 'hidden';
      inner.hidden = state === 'hidden';
      inner.classList.toggle('inventory--low', state === 'low');
      inner.classList.toggle('inventory--ok', state === 'ok');
      if (state === 'low') text.textContent = box.dataset.lowText.replace('[count]', v.inventory);
      if (state === 'ok') text.textContent = box.dataset.okText;
      if (bar) {
        bar.hidden = state !== 'low';
        const span = $('span', bar);
        if (span && state === 'low') span.style.width = Math.max(6, Math.min(100, (v.inventory * 100) / threshold)) + '%';
      }
    }

    setQuantity(qty) {
      if (this.qtyHidden) this.qtyHidden.value = qty;
      if (this.mainQty && Number(this.mainQty.value) !== qty) this.mainQty.value = qty;
      $$('[data-pack]', this).forEach((p) => { p.checked = Number(p.dataset.qty) === qty; });
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Galería de producto ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = $('[data-gallery-slider]', this);
      if (!this.slider) return;
      this.slides = Array.from(this.slider.children);
      this.thumbs = $$('[data-gallery-thumbs] [data-index]', this);
      this.dotsWrap = $('[data-gallery-dots]', this);
      this.index = 0;

      if (this.dotsWrap && this.slides.length > 1) {
        this.dotsWrap.innerHTML = this.slides.map((_, i) => `<button type="button" aria-label="${i + 1}"${i === 0 ? ' aria-current="true"' : ''}></button>`).join('');
        this.dots = Array.from(this.dotsWrap.children);
        this.dots.forEach((d, i) => d.addEventListener('click', () => this.goTo(i)));
      }
      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.index))));
      const prev = $('[data-gallery-prev]', this);
      const next = $('[data-gallery-next]', this);
      prev && prev.addEventListener('click', () => this.goTo(Math.max(0, this.index - 1)));
      next && next.addEventListener('click', () => this.goTo(Math.min(this.slides.length - 1, this.index + 1)));
      if (this.slides.length < 2) $('.gallery__arrows', this)?.remove();

      let ticking = false;
      this.slider.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const w = this.slides[0].getBoundingClientRect().width || 1;
          const gap = this.slides[1] ? this.slides[1].offsetLeft - this.slides[0].offsetLeft - w : 0;
          this.setActive(Math.round(this.slider.scrollLeft / (w + gap)));
          ticking = false;
        });
      }, { passive: true });

      // Ampliar imagen
      this.lightbox = $('[data-lightbox]', this);
      if (this.lightbox && this.dataset.zoom === 'true') {
        this.slider.addEventListener('click', (e) => {
          const slide = e.target.closest('.gallery__slide');
          if (!slide || !$('img', slide) || e.target.closest('video, model-viewer, iframe')) return;
          this.openLightbox(slide.dataset.index);
        });
        $('[data-zoom-close]', this.lightbox).addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('click', (e) => { if (e.target.tagName === 'IMG' || e.target === this.lightbox) this.lightbox.close(); });
      }
    }

    setActive(i) {
      if (i === this.index || i < 0 || i >= this.slides.length) return;
      this.index = i;
      this.thumbs.forEach((t) => t.setAttribute('aria-current', String(Number(t.dataset.index) === i)));
      const active = this.thumbs.find((t) => Number(t.dataset.index) === i);
      if (active && active.parentElement.scrollWidth > active.parentElement.clientWidth) {
        active.parentElement.scrollTo({ left: active.offsetLeft - active.parentElement.clientWidth / 2 + active.clientWidth / 2, behavior: 'smooth' });
      }
      (this.dots || []).forEach((d, idx) => d.setAttribute('aria-current', String(idx === i)));
      $$('video', this.slider).forEach((v) => { if (!this.slides[i].contains(v)) v.pause(); });
      const video = $('video', this.slides[i]);
      if (video) video.play().catch(() => {});
    }

    goTo(i) {
      const slide = this.slides[i];
      if (!slide) return;
      const scrollable = this.slider.scrollWidth > this.slider.clientWidth + 2;
      if (scrollable) {
        this.slider.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: 'smooth' });
      }
      this.setActive(i);
      this.index = i;
    }

    goToMedia(mediaId) {
      const idx = this.slides.findIndex((s) => String(s.dataset.mediaId) === String(mediaId));
      if (idx > -1) this.goTo(idx);
    }

    openLightbox(index) {
      if (typeof this.lightbox.showModal !== 'function') return;
      this.lightbox.showModal();
      const target = $(`.lightbox__slide[data-index="${index}"]`, this.lightbox);
      const track = $('[data-lightbox-track]', this.lightbox);
      if (target && track) track.scrollLeft = target.offsetLeft;
    }
  }
  customElements.define('media-gallery', MediaGallery);

  /* ---------- Barra de compra fija ---------- */
  class StickyAtc extends HTMLElement {
    connectedCallback() {
      const info = this.closest('product-info');
      const target = info && $('[data-atc]', info);
      if (!target) return;
      document.documentElement.classList.add('has-sticky-atc');
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', show);
        this.setAttribute('aria-hidden', String(!show));
      });
      io.observe(target);
      const btn = $('[data-sticky-submit]', this);
      btn && btn.addEventListener('click', () => {
        const form = target.form;
        if (form) form.requestSubmit ? form.requestSubmit(target) : target.click();
      });
    }
  }
  customElements.define('sticky-atc', StickyAtc);

  /* ---------- Fecha estimada de entrega ---------- */
  class DeliveryEstimate extends HTMLElement {
    connectedCallback() {
      const min = Number(this.dataset.min) || 0;
      const max = Math.max(min, Number(this.dataset.max) || 0);
      const skip = this.dataset.skipWeekends === 'true';
      const cutoff = Number(this.dataset.cutoff) || 0;
      const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
      const addDays = (from, days) => {
        const d = new Date(from);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          if (skip && isWeekend(d)) continue;
          added++;
        }
        return d;
      };
      const now = new Date();
      let base = new Date(now);
      if ((cutoff && now.getHours() >= cutoff) || (skip && isWeekend(now))) base = addDays(now, 1);
      const fmt = new Intl.DateTimeFormat(document.documentElement.lang || 'es', { weekday: 'short', day: 'numeric', month: 'short' });
      const text = (this.dataset.template || '')
        .replace('[start]', fmt.format(addDays(base, min)))
        .replace('[end]', fmt.format(addDays(base, max)));
      const out = $('[data-delivery-text]', this);
      if (out) out.innerHTML = text;

      const cutoffEl = $('[data-delivery-cutoff]', this);
      if (cutoff && cutoffEl && !(skip && isWeekend(now))) {
        const update = () => {
          const n = new Date();
          const end = new Date(n);
          end.setHours(cutoff, 0, 0, 0);
          const diff = end - n;
          if (diff <= 0) { cutoffEl.hidden = true; return; }
          const h = Math.floor(diff / 3600000);
          const m = Math.floor((diff % 3600000) / 60000);
          cutoffEl.innerHTML = (this.dataset.cutoffTemplate || '').replace('[time]', (h ? h + ' h ' : '') + m + ' min');
          cutoffEl.hidden = false;
        };
        update();
        this._timer = setInterval(update, 30000);
      }
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('delivery-estimate', DeliveryEstimate);

  /* ---------- Cuenta atrás (se oculta al terminar) ---------- */
  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.end = new Date(this.dataset.end).getTime();
      if (Number.isNaN(this.end)) return;
      this.parts = { d: $('[data-d]', this), h: $('[data-h]', this), m: $('[data-m]', this), s: $('[data-s]', this) };
      this.tick();
      this._timer = setInterval(() => this.tick(), 1000);
    }
    tick() {
      const diff = this.end - Date.now();
      if (diff <= 0) {
        this.hidden = true;
        clearInterval(this._timer);
        if (this.hasAttribute('data-hide-section') && !designMode) {
          const sec = this.closest('.shopify-section');
          if (sec) sec.hidden = true;
        }
        return;
      }
      this.hidden = false;
      const pad = (n) => String(n).padStart(2, '0');
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (this.parts.d) {
        this.parts.d.textContent = pad(d);
        this.parts.d.parentElement.hidden = d === 0;
      }
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('countdown-timer', CountdownTimer);

  /* ---------- Slider genérico (carruseles de secciones) ---------- */
  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = $('.slider', this);
      if (!this.track) return;
      this.prev = $('[data-prev]', this);
      this.next = $('[data-next]', this);
      this.dotsWrap = $('[data-dots]', this);
      this.prev && this.prev.addEventListener('click', () => this.move(-1));
      this.next && this.next.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      window.addEventListener('resize', debounce(() => this.buildDots(), 200));
      this.buildDots();
      this.update();

      const autoplay = Number(this.dataset.autoplay);
      if (autoplay && !designMode) {
        const start = () => { this._auto = setInterval(() => this.move(1, true), autoplay * 1000); };
        const stop = () => clearInterval(this._auto);
        this.addEventListener('mouseenter', stop);
        this.addEventListener('mouseleave', start);
        this.addEventListener('touchstart', stop, { passive: true });
        start();
      }
    }
    slideWidth() {
      const items = this.track.children;
      if (items.length < 2) return this.track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }
    pages() {
      const w = this.slideWidth() || 1;
      const perView = Math.max(1, Math.round(this.track.clientWidth / w));
      return Math.max(1, this.track.children.length - perView + 1);
    }
    buildDots() {
      if (!this.dotsWrap) return;
      const pages = this.pages();
      this.dotsWrap.innerHTML = pages > 1 ? Array.from({ length: pages }, (_, i) => `<button type="button" aria-label="${i + 1}"></button>`).join('') : '';
      Array.from(this.dotsWrap.children).forEach((d, i) => d.addEventListener('click', () => this.track.scrollTo({ left: i * this.slideWidth(), behavior: 'smooth' })));
      this.update();
    }
    move(dir, loop = false) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      if (loop && dir > 0 && this.track.scrollLeft >= max - 4) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      this.track.scrollBy({ left: dir * this.slideWidth(), behavior: 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = left <= 4;
      if (this.next) this.next.disabled = left >= max - 4;
      const nav = $('.slider-nav', this);
      if (nav) nav.hidden = max <= 4;
      if (this.dotsWrap) {
        const i = Math.round(left / (this.slideWidth() || 1));
        Array.from(this.dotsWrap.children).forEach((d, idx) => d.setAttribute('aria-current', String(idx === i)));
      }
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Barra de anuncios ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = $$('[data-announcement]', this);
      if (this.slides.length < 2 || this.dataset.mode === 'marquee') return;
      this.index = 0;
      $('[data-prev]', this)?.addEventListener('click', () => this.show(this.index - 1));
      $('[data-next]', this)?.addEventListener('click', () => this.show(this.index + 1));
      const every = (Number(this.dataset.autoplay) || 5) * 1000;
      if (!designMode) this._timer = setInterval(() => this.show(this.index + 1), every);
    }
    show(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, idx) => s.classList.toggle('is-active', idx === this.index));
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Antes / después (arrastrar, teclado, pista animada, pestañas) ---------- */
  class BeforeAfter extends HTMLElement {
    connectedCallback() {
      this.range = $('input[type="range"]', this);
      this.vertical = this.dataset.direction === 'vertical';
      this.set(Number(this.range ? this.range.value : 50));
      if (this.range) this.range.addEventListener('input', () => this.set(Number(this.range.value)));

      const move = (e) => {
        const r = this.getBoundingClientRect();
        const p = this.vertical ? (e.clientY - r.top) / r.height : (e.clientX - r.left) / r.width;
        this.set(Math.max(0, Math.min(100, p * 100)));
      };
      this.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        const onHandle = e.target.closest('.ba__handle, .ba__line');
        // En móvil (vertical) solo se arrastra desde el tirador para no bloquear el scroll
        this.dragging = !this.vertical || e.pointerType === 'mouse' || !!onHandle;
        this.stopHint = true;
        move(e);
        if (this.dragging) this.setPointerCapture(e.pointerId);
      });
      this.addEventListener('pointermove', (e) => { if (this.dragging) move(e); });
      const end = () => { this.dragging = false; };
      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      if (this.dataset.hint === 'true' && !designMode && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        onVisible(this, () => this.hint(), { threshold: 0.6 });
      }
    }
    set(v) {
      this.pos = v;
      this.style.setProperty('--pos', v + '%');
      if (this.range) this.range.value = Math.round(v);
    }
    hint() {
      const start = this.pos;
      const t0 = performance.now();
      const dur = 1600;
      const step = (t) => {
        if (this.stopHint) return;
        const p = Math.min(1, (t - t0) / dur);
        this.set(start + Math.sin(p * Math.PI * 2) * 14 * (1 - p));
        if (p < 1) requestAnimationFrame(step);
        else this.set(start);
      };
      requestAnimationFrame(step);
    }
  }
  customElements.define('before-after', BeforeAfter);

  document.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-ba-tab]');
    if (!tab) return;
    const group = tab.closest('[data-ba-group]');
    $$('[data-ba-tab]', group).forEach((t) => t.setAttribute('aria-selected', String(t === tab)));
    $$('[data-ba-panel]', group).forEach((p) => { p.hidden = p.dataset.baPanel !== tab.dataset.baTab; });
  });

  /* ---------- Reseñas con fotos: ver más, filtro y foto ampliada ---------- */
  class PhotoReviews extends HTMLElement {
    connectedCallback() {
      this.step = Number(this.dataset.step) || 8;
      this.shown = this.step;
      this.filter = 'all';
      this.modal = $('[data-pr-modal]', this);
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-pr-more]')) { this.shown += this.step; this.update(); return; }
        const f = e.target.closest('[data-filter]');
        if (f) {
          this.filter = f.dataset.filter;
          $$('[data-filter]', this).forEach((b) => b.setAttribute('aria-pressed', String(b === f)));
          this.shown = this.step;
          this.update();
          return;
        }
        const open = e.target.closest('[data-pr-open]');
        if (open && this.modal && this.modal.showModal) {
          const tpl = $('template[data-pr-full]', open.closest('.pr-card'));
          $('[data-pr-modal-inner]', this.modal).innerHTML = tpl ? tpl.innerHTML : '';
          this.modal.showModal();
          return;
        }
        if (e.target.closest('[data-pr-close]') || e.target === this.modal) this.modal.close();
      });
    }
    update() {
      if (this.dataset.layout === 'carousel') return;
      const cards = $$('.pr-card', this).filter((c) => this.filter === 'all' || c.dataset.hasPhoto === 'true');
      $$('.pr-card', this).forEach((c) => c.classList.add('is-hidden'));
      cards.forEach((c, i) => c.classList.toggle('is-hidden', i >= this.shown));
      const more = $('[data-pr-more]', this);
      if (more) more.parentElement.hidden = cards.length <= this.shown;
    }
  }
  customElements.define('photo-reviews', PhotoReviews);

  /* ---------- Vídeos UGC: se reproducen al verse, sonido al pulsar ---------- */
  class UgcVideo extends HTMLElement {
    connectedCallback() {
      this.video = $('video', this);
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;
      const io = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          if (this.video.preload === 'none') this.video.preload = 'metadata';
          this.video.play().catch(() => {});
        } else {
          this.video.pause();
        }
      }, { threshold: 0.4 });
      io.observe(this);
      const btn = $('[data-mute]', this);
      btn && btn.addEventListener('click', () => {
        const unmute = this.video.muted;
        $$('ugc-video video').forEach((v) => { v.muted = true; v.closest('ugc-video').classList.remove('is-unmuted'); });
        this.video.muted = !unmute;
        this.classList.toggle('is-unmuted', unmute);
        if (unmute) this.video.play().catch(() => {});
      });
    }
  }
  customElements.define('ugc-video', UgcVideo);

  /* ---------- Contador animado de estadísticas ---------- */
  class CountUp extends HTMLElement {
    connectedCallback() {
      const raw = this.textContent.trim();
      const m = raw.match(/\d[\d.,]*/);
      if (!m || designMode || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      const numStr = m[0].replace(/[.,]$/, '');
      const lastSep = Math.max(numStr.lastIndexOf(','), numStr.lastIndexOf('.'));
      const decSep = lastSep > -1 && numStr.length - lastSep - 1 <= 2 ? numStr[lastSep] : null;
      const decimals = decSep ? numStr.length - lastSep - 1 : 0;
      const thouSep = decSep === ',' ? '.' : decSep === '.' ? ',' : (numStr.includes('.') ? '.' : ',');
      const target = parseFloat(numStr.split(thouSep).join('').replace(decSep || '#', '.'));
      if (!Number.isFinite(target)) return;
      const grouped = numStr.includes(thouSep);
      const format = (n) => {
        const [int, dec] = n.toFixed(decimals).split('.');
        const i = grouped ? int.replace(/\B(?=(\d{3})+(?!\d))/g, thouSep) : int;
        return dec ? i + decSep + dec : i;
      };
      const [before, after] = [raw.slice(0, m.index), raw.slice(m.index + numStr.length)];
      onVisible(this, () => {
        const t0 = performance.now();
        const dur = 1600;
        const step = (t) => {
          const p = Math.min(1, (t - t0) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          this.textContent = before + format(target * eased) + after;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }
  }
  customElements.define('count-up', CountUp);


  /* ---------- Pestañas genéricas ---------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const tab = e.target.closest('[data-tab]');
        if (!tab || tab.closest('tabs-component') !== this) return;
        this.select(tab.dataset.tab);
      });
      this.addEventListener('keydown', (e) => {
        if (!['ArrowRight', 'ArrowLeft'].includes(e.key) || !e.target.matches('[data-tab]')) return;
        const tabs = $$('[data-tab]', this).filter((t) => t.closest('tabs-component') === this);
        const i = tabs.indexOf(e.target) + (e.key === 'ArrowRight' ? 1 : -1);
        const next = tabs[(i + tabs.length) % tabs.length];
        next.focus();
        this.select(next.dataset.tab);
      });
    }
    select(id) {
      $$('[data-tab]', this).forEach((t) => { if (t.closest('tabs-component') === this) t.setAttribute('aria-selected', String(t.dataset.tab === id)); });
      $$('[data-tab-panel]', this).forEach((p) => { if (p.closest('tabs-component') === this) p.hidden = p.dataset.tabPanel !== id; });
    }
  }
  customElements.define('tabs-component', TabsComponent);

  /* ---------- Imagen con puntos ---------- */
  class HotspotImage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (e) => {
        const pin = e.target.closest('.hotspot__pin');
        const spots = $$('.hotspot', this);
        if (!pin) { spots.forEach((h) => this.toggle(h, false)); return; }
        const spot = pin.closest('.hotspot');
        const open = !spot.classList.contains('is-open');
        spots.forEach((h) => this.toggle(h, false));
        this.toggle(spot, open);
      });
    }
    toggle(spot, open) {
      spot.classList.toggle('is-open', open);
      $('.hotspot__pin', spot).setAttribute('aria-expanded', String(open));
    }
  }
  customElements.define('hotspot-image', HotspotImage);

  /* ---------- Copiar código ---------- */
  class CopyCode extends HTMLElement {
    connectedCallback() {
      const btn = $('[data-copy]', this);
      if (!btn) return;
      btn.addEventListener('click', async () => {
        const codeEl = $('[data-code]', this);
        const code = codeEl ? codeEl.textContent.trim() : '';
        try {
          await navigator.clipboard.writeText(code);
        } catch (e) {
          const r = document.createRange();
          r.selectNodeContents(codeEl);
          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(r);
          document.execCommand('copy');
        }
        const label = $('span', btn) || btn;
        const old = label.textContent;
        label.textContent = this.dataset.copied || '✓';
        this.classList.add('is-copied');
        setTimeout(() => { label.textContent = old; this.classList.remove('is-copied'); }, 2000);
      });
    }
  }
  customElements.define('copy-code', CopyCode);

  /* ---------- Popup (retraso, scroll o intención de salida; frecuencia) ---------- */
  class PopupModal extends HTMLElement {
    connectedCallback() {
      this.dialog = $('dialog', this);
      if (!this.dialog) return;
      this.key = 'unico-popup-' + this.dataset.id;
      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-popup-close]') || e.target === this.dialog) this.close();
      });
      this.dialog.addEventListener('close', () => this.remember());
      if (designMode) return;
      if (this.dataset.mobile === 'false' && window.matchMedia('(max-width: 749px)').matches) return;
      // Tras suscribirse, Shopify recarga la página: volver a mostrar el popup con el mensaje de éxito
      if (window.location.search.includes('customer_posted=true')) { this.open(); return; }
      if (this.seen()) return;
      const trigger = this.dataset.trigger;
      if (trigger === 'scroll') {
        const onScroll = () => {
          const max = document.documentElement.scrollHeight - window.innerHeight;
          if (max > 0 && (window.scrollY / max) * 100 >= Number(this.dataset.scroll)) {
            window.removeEventListener('scroll', onScroll);
            this.open();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else if (trigger === 'exit') {
        const onOut = (e) => {
          if (e.clientY <= 0) { document.removeEventListener('mouseout', onOut); this.open(); }
        };
        document.addEventListener('mouseout', onOut);
        // En móvil no existe «salir con el ratón»: se muestra tras 25 s
        if (window.matchMedia('(hover: none)').matches) setTimeout(() => this.open(), 25000);
      } else {
        setTimeout(() => this.open(), (Number(this.dataset.delay) || 8) * 1000);
      }
    }
    seen() {
      try {
        const t = Number(localStorage.getItem(this.key));
        const days = Number(this.dataset.days);
        return !!t && days > 0 && Date.now() - t < days * 86400000;
      } catch (e) {
        return false;
      }
    }
    remember() {
      try { localStorage.setItem(this.key, String(Date.now())); } catch (e) { /* sin almacenamiento */ }
    }
    open() {
      if (this.dialog.open || document.querySelector('cart-drawer.is-open')) return;
      if (this.dialog.showModal) this.dialog.showModal();
    }
    close() {
      if (this.dialog.open) this.dialog.close();
    }
  }
  customElements.define('popup-modal', PopupModal);
  document.addEventListener('shopify:section:select', (e) => {
    const p = e.target.querySelector('popup-modal');
    if (p && p.dialog && p.dialog.showModal && !p.dialog.open) p.dialog.showModal();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const p = e.target.querySelector('popup-modal');
    if (p && p.close) p.close();
  });

  /* ---------- Ventanas modales (guía de tallas, información…) ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.modalOpen);
      if (modal && modal.showModal) modal.showModal();
      return;
    }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) { closer.closest('dialog').close(); return; }
    if (e.target.matches('dialog.modal')) e.target.close();
  });

  /* ---------- Casilla de términos del carrito ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('.cart__footer');
    if (!form) return;
    const terms = form.querySelector('[data-cart-terms]');
    if (terms && !terms.checked) {
      e.preventDefault();
      form.classList.add('needs-terms');
      terms.focus();
      toast(terms.dataset.message);
    }
  }, true);
  document.addEventListener('change', (e) => {
    if (e.target.matches('[data-cart-terms]')) e.target.closest('.cart__footer').classList.toggle('needs-terms', !e.target.checked);
  });

  /* ---------- Botón volver arriba ---------- */
  const initBackToTop = () => {
    const btn = $('[data-back-to-top]');
    if (!btn) return;
    const toggle = () => btn.classList.toggle('is-visible', window.scrollY > window.innerHeight * 1.5);
    toggle();
    window.addEventListener('scroll', toggle, { passive: true });
    btn.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  /* ---------- Cabecera ---------- */
  const initHeader = () => {
    const header = $('.site-header');
    if (!header) return;
    const wrap = header.closest('.shopify-section') || header;
    const setH = () => document.documentElement.style.setProperty('--header-h', header.offsetHeight + 'px');
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(header);
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    if (header.dataset.sticky === 'true') wrap.classList.add('header-wrapper--sticky');

    // Menú móvil: cerrar al pulsar un enlace
    $$('.menu-drawer a', header).forEach((a) => a.addEventListener('click', () => {
      const d = a.closest('details');
      if (d) d.open = false;
    }));
  };

  /* ---------- Animación al hacer scroll ---------- */
  const initReveal = (root = document) => {
    const items = $$('.reveal:not(.is-visible)', root);
    if (!document.documentElement.classList.contains('has-reveal') || designMode || !('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    items.forEach((el) => io.observe(el));
  };

  /* ---------- Editor de temas ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    if (e.target.querySelector('.site-header')) initHeader();
  });
  document.addEventListener('shopify:block:select', (e) => {
    const bar = e.target.closest('announcement-bar');
    if (bar && bar.show) bar.show(Array.from(bar.querySelectorAll('[data-announcement]')).indexOf(e.target.closest('[data-announcement]') || e.target));
  });

  /* ---------- Inicio ---------- */
  const init = () => {
    initHeader();
    initReveal();
    initBackToTop();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();

/* ---------- Producto destacado ---------- */
(() => {
  if (customElements.get('featured-product')) return;
  class FeaturedProduct extends HTMLElement {
    connectedCallback() {
      this.img = this.querySelector('.fp__img');
      this.addEventListener('click', (e) => {
        const t = e.target.closest('[data-fp-thumb]');
        if (!t) return;
        this.querySelectorAll('[data-fp-thumb]').forEach((b) => b.classList.toggle('is-active', b === t));
        this.show(t.dataset.src, t.dataset.srcset);
      });
      this.addEventListener('change', (e) => {
        const r = e.target;
        if (!r.matches('input[type="radio"][name="id"]')) return;
        const tpl = this.querySelector(`template[data-fp-price-for="${r.value}"]`);
        const price = this.querySelector('[data-fp-price]');
        if (tpl && price) price.innerHTML = tpl.innerHTML;
        if (r.dataset.image) this.show(r.dataset.image, '');
        const btn = this.querySelector('[data-atc]');
        if (btn) {
          const ok = r.dataset.available === 'true';
          btn.disabled = !ok;
          const label = btn.querySelector('[data-atc-label]');
          if (label) label.textContent = ok ? btn.dataset.label : btn.dataset.soldOut;
        }
      });
    }
    show(src, srcset) {
      if (!this.img || !src) return;
      this.img.classList.add('is-swapping');
      const done = () => this.img.classList.remove('is-swapping');
      this.img.addEventListener('load', done, { once: true });
      if (srcset) this.img.srcset = srcset; else this.img.removeAttribute('srcset');
      this.img.src = src;
      setTimeout(done, 600);
    }
  }
  customElements.define('featured-product', FeaturedProduct);
})();
